import { Box, Link, styled } from '@mui/material';
import { Link as RouterLink, generatePath } from 'react-router';

import routes from '@/assets/constants/routes-no.ts';

import { SIZES } from '../../../../assets/constants/constants.ts';
import ActionButton from '../../../../components/ActionButton/index.ts';
import Section from '../../../../components/Section/index.ts';

const ArticleSectionsContainer = styled(Box)(({ theme }) => ({
  maxWidth: SIZES.layout.width.sm,
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
  },
}));

const ArticleSection = styled(Box)({
  maxWidth: SIZES.layout.width.sm,
});

const Title = styled('h1')(({ theme }) => ({
  marginBottom: theme.spacing(3),
  [theme.breakpoints.up('sm')]: {
    marginBottom: theme.spacing(4),
  },
}));

const Subtitle = styled('h3')(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const Text = styled('p')(({ theme }) => ({
  marginBottom: theme.spacing(4),
  [theme.breakpoints.up('sm')]: {
    marginBottom: theme.spacing(7),
  },
}));

const StyledLink = styled(Link)(() => ({
  fontSize: '1rem',
  lineHeight: 1.5,
})) as typeof Link;

const List = styled('ul')(({ theme }) => ({
  fontSize: '1rem',
  lineHeight: 1.5,
  paddingLeft: theme.spacing(3.5),
}));

export default function RefinancingArticle() {
  return (
    <>
      <Section>
        <ArticleSection>
          <Title className="typography-v1-legacy-h2 text-v1-primary-100">Refinansiering</Title>
          <Subtitle className="typography-v1-legacy-subtitle1Bold">
            Refinansiering handler om å samle smålån i et gunstigere felleslån. Men hva betyr det
            egentlig, og hvordan gjør du det? Vi guider deg gjennom det du trenger å vite om
            refinansiering.
          </Subtitle>
          <p className="typography-v1-legacy-subtitle2">
            Har du mer enn ett lån? Da er du ikke alene. Boliglån og billån er vanlig, og i tillegg
            har vi gjerne forbrukslån og kredittkort. Ulempen med denne typen smålån er at de som
            regel er dyre. Da er det lurt å se på en refinansiering – det kan nemlig være tusener å
            spare hver måned. Men la oss først ta et dypdykk i hva refinansiering er.
          </p>
        </ArticleSection>
      </Section>
      <Section background="paper">
        <ArticleSectionsContainer>
          <Box
            sx={{
              pr: { xs: 0, sm: 3 },
              width: { xs: '100%', sm: '50%' },
            }}
          >
            <Subtitle className="typography-v1-legacy-subtitle1Bold">
              Hva er refinansiering?
            </Subtitle>
            <Text className="typography-v1-legacy-p3">
              Enkelt forklart er refinansiering å samle mange og dyre smålån med høy rente, til ett
              større og billigere lån med lavere rente. Disse smålånene kan være forbrukslån,
              kredittkortgjeld og handlekontoer.
            </Text>

            <Subtitle className="typography-v1-legacy-subtitle1Bold">
              Refinansiere forbrukslån?
            </Subtitle>
            <Text className="typography-v1-legacy-p3">
              Har du mange forbrukslån, vil det sannsynligvis lønne seg å refinansiere. Du bør
              likevel vite at ikke alle får lavere rente, fordi det blant annet avhenger av
              økonomien og betalingsevnen din. Når det er sagt, får de aller fleste lavere rente enn
              de har på smålånene sine.
              <br />
              <br />
              Synes du det er vanskelig å orientere deg i jungelen av banker som tilbyr
              refinansiering av forbrukslån? Det er her{' '}
              <StyledLink component={RouterLink} to={routes.HOME}>
                Scoopr
              </StyledLink>{' '}
              kommer inn i bildet. For det er enkelt å sjekke om du kan få lavere rente med en
              refinansiering, og det er enkelt å bytte avtale.
            </Text>

            <Subtitle className="typography-v1-legacy-subtitle1Bold">
              Hvordan refinansiere forbrukslån?
            </Subtitle>
            <p className="typography-v1-legacy-p3">
              Scoopr gjør nemlig jobben for deg – altså sender søknaden din til flere banker. For å{' '}
              <StyledLink
                component={RouterLink}
                to={generatePath(routes.PRODUCTS_FAQ_ARTICLE, {
                  articleName: 'hvordan-soker-jeg-pa-refinansiering-hos-scoopr',
                })}
              >
                refinansiere forbrukslån hos Scoopr
              </StyledLink>
              , må du oppfylle noen minstekrav.
            </p>
            <List>
              <li>Du må være over 18 år</li>
              <li>Du må ha et norsk personnummer</li>
              <li>Du må kunne vise til en årlig inntekt på minst 140.000 kroner før skatt</li>
              <li>Du kan ikke ha noen betalingsanmerkninger</li>
            </List>
            <Text className="typography-v1-legacy-p3">
              Hvis du har alt dette på plass, kan du gå videre for å søke om refinansiering.
            </Text>
          </Box>

          <Box
            sx={{
              pl: { xs: 0, sm: 3 },
              width: { xs: '100%', sm: '50%' },
            }}
          >
            <Subtitle className="typography-v1-legacy-subtitle1Bold">
              Hva betyr refinansiering?
            </Subtitle>
            <p className="typography-v1-legacy-p3">
              Refinansiering betyr at du får en bedre deal på ett lån enn du får på mange smålån.
              Med andre ord kan du kutte ned på beløpet du betaler hver måned, og spare penger. Når
              du refinansierer, får du flere fordeler:
            </p>
            <List>
              <li>Du kan få lavere rente</li>
              <li>Du får færre gebyrer</li>
              <li>Du får én bank å forholde deg til</li>
              <li>Du får én månedlig regning</li>
              <li>Du unngår unødvendige kostnader</li>
              <li>Du får bedre oversikt</li>
              <li>Du får lavere totale kostnader</li>
              <li>Du kan begynne å spare penger til en buffer</li>
            </List>
            <Text className="typography-v1-legacy-p3">
              Alt i alt – med refinansiering trenger du ikke å betale mer enn du må.
            </Text>

            <Subtitle className="typography-v1-legacy-subtitle1Bold">
              Hvordan søke refinansiering?
            </Subtitle>
            <Text className="typography-v1-legacy-p3">
              Hos Scoopr kan du søke om refinansiering fra 20.000 til 500.000 kroner. Det er enkelt,
              gratis og uforpliktende, og tar deg kun to minutter.
              <br />
              <br />
              <StyledLink
                component={RouterLink}
                to={generatePath(routes.PRODUCTS_FAQ_ARTICLE, {
                  articleName: 'hvordan-soker-jeg-pa-refinansiering-hos-scoopr',
                })}
              >
                Du søker refinansiering her
              </StyledLink>
              . Legg inn opplysningene dine, og så blir søknaden automatisk sendt til 13 banker. De
              gjør en individuell vurdering av betalingsevnen din der de blant annet sjekker
              inntekt, betjeningsevne, kredittverdighet, om du allerede har gjeld og hvor høy den
              er.
              <br />
              <br />
              Som regel får du svar på søknaden din etter kort tid. Hvis søknaden blir godkjent
              trenger du ikke å gjøre noe mer –{' '}
              <StyledLink
                component={RouterLink}
                to={generatePath(routes.PRODUCTS_FAQ_ARTICLE, {
                  articleName: 'betaler-dere-ut-min-eksisterende-gjeld',
                })}
              >
                da betaler normalt banken den gamle gjelden din
              </StyledLink>
              . Du sparer både tid og arbeid, og i tillegg kan du være sikker på at den gamle og
              dyre gjelden din faktisk blir nedbetalt.
            </Text>
          </Box>
        </ArticleSectionsContainer>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <h3 className="mx-auto mt-14 mb-8 max-w-225 text-center typography-v1-legacy-subtitle1Bold">
            Sjekk hvor mye du kan spare på refinansiering hos Scoopr i dag.
          </h3>

          <ActionButton
            label="Søk refinansiering hos Scoopr"
            flow="debt_check"
            step="skip_start_set_savings_potential_mode"
            color="secondary"
          />
        </Box>
      </Section>
    </>
  );
}
